interface Invoice {
  id: string;
  invoiceNumber: string;
  jobId: string;
  providerId: string;
  customerId: string;
  amount: number;
  workDescription: string;
  paymentDetails?: string;
  status: 'UNPAID' | 'PAID' | 'CANCELLED';
  issueDate: Date;
  dueDate?: Date;
  paidDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  
  // Denormalized data
  jobTitle: string;
  customerName: string;
  customerEmail: string;
  providerName: string;
  providerEmail: string;
  providerPhone?: string;
  providerAddress?: string;
}

interface CreateInvoiceRequest {
  jobId: string;
  finalAmount: number;
  workDescription: string;
  paymentDetails?: string;
}

class InvoiceService {
  private readonly API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Create invoice when completing a job
  async createInvoice(data: CreateInvoiceRequest): Promise<Invoice> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/invoices`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to create invoice' }));
      throw new Error(errorData.error || 'Failed to create invoice');
    }

    const result = await response.json();
    return this.mapInvoice(result.data);
  }

  // Get provider's invoices
  async getProviderInvoices(providerId: string, status?: string): Promise<Invoice[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    
    const url = `${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/invoices${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch invoices');
    }

    const result = await response.json();
    return (result.data || []).map(this.mapInvoice);
  }

  // Get customer's invoices
  async getCustomerInvoices(customerId: string, status?: string): Promise<Invoice[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    
    const url = `${this.API_BASE_URL}/api/v1/auth/customers/${customerId}/invoices${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch invoices');
    }

    const result = await response.json();
    return (result.data || []).map(this.mapInvoice);
  }

  // Get single invoice
  async getInvoice(invoiceId: string): Promise<Invoice> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/invoices/${invoiceId}`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to fetch invoice');
    }

    const result = await response.json();
    return this.mapInvoice(result.data);
  }

  // Update invoice status (mark as paid)
  async updateInvoiceStatus(invoiceId: string, status: 'PAID' | 'CANCELLED'): Promise<Invoice> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/invoices/${invoiceId}/status`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status })
    });

    if (!response.ok) {
      throw new Error('Failed to update invoice status');
    }

    const result = await response.json();
    return this.mapInvoice(result.data);
  }

  // Resend invoice email
  async resendInvoice(invoiceId: string): Promise<void> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/invoices/${invoiceId}/resend`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to resend invoice');
    }
  }

  // Download invoice PDF
  async downloadInvoicePDF(invoiceId: string): Promise<Blob> {
    const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/invoices/${invoiceId}/pdf`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to download invoice');
    }

    return await response.blob();
  }

  // Helper to map API response to Invoice type
  private mapInvoice(data: any): Invoice {
    return {
      id: data.id,
      invoiceNumber: data.invoice_number || data.invoiceNumber,
      jobId: data.job_id || data.jobId,
      providerId: data.provider_id || data.providerId,
      customerId: data.customer_id || data.customerId,
      amount: data.amount,
      workDescription: data.work_description || data.workDescription,
      paymentDetails: data.payment_details || data.paymentDetails,
      status: data.status,
      issueDate: new Date(data.issue_date || data.issueDate),
      dueDate: data.due_date ? new Date(data.due_date) : undefined,
      paidDate: data.paid_date ? new Date(data.paid_date) : undefined,
      createdAt: new Date(data.created_at || data.createdAt),
      updatedAt: new Date(data.updated_at || data.updatedAt),
      jobTitle: data.job_title || data.jobTitle,
      customerName: data.customer_name || data.customerName,
      customerEmail: data.customer_email || data.customerEmail,
      providerName: data.provider_name || data.providerName,
      providerEmail: data.provider_email || data.providerEmail,
      providerPhone: data.provider_phone || data.providerPhone,
      providerAddress: data.provider_address || data.providerAddress
    };
  }
}

export const invoiceService = new InvoiceService();
export type { Invoice, CreateInvoiceRequest };
